<template>
	<ModalDialog
		:buttons="[[{name: 'cancel', caption: $t('cancel'), classes: 'btn-outline-secondary', clickFn: cancelClickedHandler, isDisabled: false}],
					[{name: 'ok', caption: okButtoncaption, classes: 'btn-success', clickFn: okClickedHandler, isDisabled: !isModalOkEnabled}]]"
		@escKeyPressed="escKeyPressedHandler"
		@modalDialogMounted="modalDialogMountedHandler">
		<template v-slot:title>{{ isNaN(item.id) ? $t(`modal_add_document_link_title`) : $t(`modal_edit_document_link_title`) }}</template>
		<template v-slot:body>
			<div class="row mb-2 mt-3">
				<div class="col-4">
					<label class="col-form-label">{{ $t('document_name') }}</label>
				</div>
				<div class="col-8"></div>
				<div class="col-4">
					<input type="text" class="form-control" ref="itemTitle" :disabled="item.linkExists && isEditing || loadingData"
						v-model="item.title"
						@blur= "checkFileExists"
						@keydown.enter= "checkFileExists"
						@keydown.esc="cancelClickedHandler">
				</div>
				<div class="col-8 d-flex align-items-center">
					<div class="row">
						<div class="col-12">
							<input type="checkbox" :id="item.title + '-checkbox'" class="ml-1 mr-1" v-model="item.pendrive">
							{{ $t('include_document_in_pendrive') }}
						</div>
						<div class="col-12">
							<input type="checkbox" :id="item.title + '-checkbox-altres'" class="ml-1 mr-1" v-model="item.otherDocuments" :disabled="isEditing" @change="onInputOtherDocuments">
							{{ $t('other_documents') }}
						</div>
					</div>
				</div>
			</div>
			<div class="row mb-2">
				<div class="col-6">
					<label class="col-form-label">{{ $t('category') }}</label>
					<select class="form-control"
						v-model="categoryId"
						@change="productCategorySelectedHandler"
						:disabled="!isEditing || loadingData">
						<option v-for="option in documentationCategories" :value="option.id" :key="option.id">{{ option.title }}</option>
					</select>
				</div>
				<div class="col-6">
					<label class="col-form-label">{{ $t('subcategory') }}</label>
					<select class="form-control" v-model="subcategoryId" :disabled="!isEditing || loadingData" @change="productSubCategorySelectedHandler">
						<option v-for="option in documentationSubcategoriesFiltered" :value="option.id" :key="option.id">{{ option.title }}</option>
					</select>
				</div>
			</div>
			<div v-if="(item.otherDocuments || item.certificationId)" class="row mb-2">
				<div class="col-6">
					<label class="col-form-label">{{ $t('certification') }}</label>
					<select class="form-control"
						v-model="item.certificationId"
						:disabled="!isEditing || loadingData">
						<option v-for="option in documentationCertifications" :value="option.id" :key="option.id">{{ option.title }}</option>
					</select>
				</div>
			</div>
			<div class="row mb-2">
				<div class="col-12">
					<label class="col-form-label">{{ $t('language') }}</label>
					<select class="form-control" v-model="item.languageId" :disabled="!item.languageEditEnabled">
						<option v-for="lang in languages" :value="lang.id"  :key="lang.id">{{ lang.languagesScreen }}</option>
					</select>
				</div>
			</div>
			<div class="row mb-2">
				<div class="col-12">
					<label class="col-form-label">{{ $t('description') }}</label>
					<TextareaAutosize v-model="item.description" :max-height="maxHeight" :min-height="minHeight" readonly class="form-control w-100 p-1"></TextareaAutosize>
				</div>
			</div>
			<template v-if="isNaN(item.id)">
				<div class="row mb-2">
					<div class="col-12 has-elements-visible-on-hover">
						<label class="col-form-label">{{ $t('series_versions_selected') }} </label>
						<i class="fas fa-edit ml-1 text-primary visible-on-hover" :style="{'display': nameChecked ? 'inline-block' : 'none' }">
							<tippy-component
								:animate-fill="false"
								arrow
								animation="fade"
								distant="7"
								hideOnClick=true
								interactive
								maxWidth="none"
								placement="right-end"
								theme="light">
								<div class="bs-slc">
									<div class="series-versions-document-included-list p-2">
										<ul class="list-unstyled">
											<li v-for="(item, index) in seriesAndVersionsList" :key="item.versionId" :class="index ? 'mt-1' : 'mt-0'">
												<div class="d-flex align-items-center text-left" :title="$t(item.title)">
													<input type="checkbox" :id="item.versionId + '-series-version-checkbox'" class="mr-2" @change="onSeriesVersionCheckboxChanged(item,$event.target.checked)" :disabled="!item.enabled" :checked="item.selected">
													<label :for="item.versionId + '-series-version-checkbox'" class="mb-0">{{ item.label }}</label>
												</div>
											</li>
										</ul>
									</div>
								</div>
							</tippy-component>
						</i>
						<TextareaAutosize :value="textSeriesVersionsSelected" :max-height="maxHeight" :min-height="minHeight" readonly class="form-control w-100 p-1"></TextareaAutosize>
					</div>
				</div>
			</template>
			<div v-else class="align-items-center">
				<label :for="'select-all-series-versions-text-area'" class="mb-0">{{ $t('series_version_to_modify') }}</label>
				<TextareaAutosize :value="textSeriesVersionsSelected" :max-height="maxHeight" readonly class="form-control w-100 p-1"></TextareaAutosize>
				<div class="d-flex align-items-center mt-1">
					<input type="checkbox" id="select-all-series-versions-checkbox" class="ml-0 mr-1">
					<label for="select-all-series-versions-checkbox" class="mb-0">{{ $t('search_and_modify_all_versions') }}</label>
				</div>
				<div v-if="selectAllSeriesVersionCheckboxChecked">
					<i class="fas fa-exclamation-triangle text-warning" :title="$t('all_documents_links_in_series_overwritten')"></i>
					<label class="ml-1">{{ $t('all_documents_links_in_series_overwritten') }}</label>
				</div>
			</div>
		</template>
		<template v-slot:errorMessage v-if="errorMessage">
				<div class="font-weight-semibold px-3 pb-3 text-danger ">{{errorMessage}}</div>
		</template>
	</ModalDialog>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import ModalDialog from './ModalDialog.vue';
import { CONSTANTS } from '../resources/constants.js';
import { utilities } from '../resources/utilities.js';

export default {
	props: ['item', 'version'],

	components: {
		ModalDialog,
	},

	data() {
		return {
			categoryId: null,
			errorMessage: null,
			itemsIdArray: [],
			loadingData: null,
			nameChecked: false,
			selectAllSeriesVersionCheckboxChecked: false,
			seriesAndVersionsList: [],
			subcategoryId: null,
			versionsSelected: [],
		};
	},

	computed: {
		...mapState([
			'documentationCategories',
			'documentationCertifications',
			'documentationSubcategories',
			'languages',
			'series',
		]),

		...mapGetters(['getLanguageByCode']),

		documentationSubcategoriesFiltered() {
			return this.documentationSubcategories.filter((sc) => sc.categoryId === this.categoryId);
		},

		isEditing() {
			if (this.item.otherDocuments || this.item.certificationId) return this.item.fileExists;
			// have to be a title with a correct formant, and also, document exists
			return this.item.hasValidFormatTitle && this.item.fileExists;
		},

		isModalOkEnabled() {
			if (this.item.otherDocuments || this.item.certificationId) return !this.loadingData && this.item.fileExists && this.subcategoryId;
			return !this.loadingData && this.item.fileExists && this.subcategoryId && this.item.languageId && (this.isEditing || this.item.hasValidFormatTitle);
		},

		maxHeight() {
			return CONSTANTS.textareaAutosize.maxHeight;
		},

		minHeight() {
			return CONSTANTS.textareaAutosize.minHeight;
		},

		okButtoncaption() {
			return (this.loadingData ? '<i class="fas fa-spinner spinning mr-2"></i>' : '') + this.$t('save');
		},

		textSeriesVersionsSelected() {
			if (this.versionsSelected.length > 0) {
				return this.versionsSelected.map((s) => s.label).join(', ');
			}

			return '';
		},
	},

	methods: {
		...mapActions([
			'getAllSeriesVersionsContainingDocumentLink',
			'getDocumentsDescriptions',
			'getLocalIP',
			'saveDocumentationLinksListItem',
		]),

		...mapMutations(['updateUsageTrackerStorage']),

		cancelClickedHandler() {
			this.$emit('cancelClicked');
		},

		checkFileExists() {
			let isValid = false;

			this.nameChecked = false;
			this.seriesAndVersionsList.forEach((svl) => {
				svl.enabled = true;
				svl.selected = false;
			});
			this.versionsSelected.splice(0); // clear the array with reactivity, if use length = 0, the computed method are not executed
			this.versionsSelected.push(this.seriesAndVersionsList.find((svl) => svl.versionId === this.version.id));

			this.itemsIdArray.splice(0); // clear the array with reactivity, if use length = 0, the computed method are not executed
			this.itemsIdArray.push(this.item.id);

			this.errorMessage = null;
			isValid = true;

			if (!this.item.otherDocuments) {
				isValid = this.checkTitleFileFormat();
			} else if (this.version.documentationLinksList.content.find((r) => r.title.includes(this.item.title))) {
				this.showError(this.$t('duplicate_title'));
				isValid = false;
			}

			if (!isValid) return;

			this.loadingData = true;

			if (this.item.otherDocuments) {
				this.subcategoryId = this.item.subcategoryId = CONSTANTS.OtherDocuments.subcategoryId;
				this.categoryId = CONSTANTS.OtherDocuments.categoryId;
			}

			this.getDocumentsDescriptions({ items: [this.item] })
				.then((responsesOk) => {
					if (!responsesOk[0]) {
						this.showError(this.$t('document_not_found'));
					} else {
						this.nameChecked = true;
						this.getAllSeriesVersionsContainingDocumentLink({ documentTitle: this.item.title }).then((response) => {
							this.seriesAndVersionsList.forEach((svl) => {
								const included = response.some(({ versionId }) => versionId === svl.versionId);
								svl.enabled = included ? !included : svl.versionId !== this.version.id;
								svl.selected = included || svl.versionId === this.version.id;
								svl.title = svl.selected ? (included ? 'document_already_included_in_series' : 'current_series_and_version') : '';
							});
						});
					}
				})
				.catch((error) => {
					console.error('[PRODUCTS] EditDocumentLink.getDocumentsDescriptions failed', error);
					this.showError(this.$t('could_not_check_document_info'));
				})
				.finally(() => {
					this.loadingData = false;
				});
		},

		checkTitleFileFormat() {
			if (!this.item.title) return false;

			this.errorMessage = null;
			this.loadingData = true;
			this.item.title = this.item.title && this.item.title.toUpperCase();

			if (!this.item.hasValidFormatTitle) {
				this.showError(this.$t('wrong_title_format'));
				return false;
			}

			// Check for title duplicate before get the description!
			if (this.version.documentationLinksList.content.find((r) => r.title === this.item.title)) {
				this.showError(this.$t('duplicate_title'));
				return false;
			}

			return true;
		},

		escKeyPressedHandler() {
			this.cancelClickedHandler();
		},

		modalDialogMountedHandler() {
			if (!this.isEditing) this.$refs.itemTitle.focus();
		},

		okClickedHandler() {
			this.loadingData = true;
			this.item.subcategoryId = this.subcategoryId;
			this.errorMessage = null;

			this.saveDocumentationLinksListItem({ item: this.item, versionsSelected: this.versionsSelected, itemsIdArray: this.itemsIdArray, currentVersionId: this.version.id })
				.then(() => {
					this.getLocalIP().then((localIp) => {
						this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.editDocumentLink, data: { documentName: this.item.title, versionId: this.versionsSelected.map((vs) => vs.versionId), seriesId: this.versionsSelected.map((vs) => vs.seriesId), isMultipleEdit: this.versionsSelected.length > 1, isEdition: !!this.item.id, isAddition: !this.item.id }, localIp });
					});

					this.$emit('okClicked');
				})
				.catch((error) => {
					this.showError(this.$t('could_not_check_document_info'));
					console.error('[PRODUCTS] Error saving DocumentLinks', error);
				})
				.finally(() => {
					this.loadingData = false;
				});
		},

		onInputOtherDocuments() {
			if (this.item.title) this.checkFileExists();
		},

		onSelectedAllSeriesVersionCheckboxChanged(checked) {
			this.selectAllSeriesVersionCheckboxChecked = checked;
			this.versionsSelected.splice(0);
			this.itemsIdArray.splice(0);

			if (checked) {
				this.loadingData = true;
				this.getAllSeriesVersionsContainingDocumentLink({ documentTitle: this.item.title }).then((response) => {
					response.forEach((r) => {
						this.itemsIdArray.push(r.documentId);
						this.versionsSelected.push(this.seriesAndVersionsList.find((svl) => svl.versionId === r.versionId));
					});
					this.loadingData = false;
				});
			} else {
				this.itemsIdArray.push(this.item.id);
				this.versionsSelected.push(this.seriesAndVersionsList.find((svl) => svl.versionId === this.version.id));
			}
		},

		onSeriesVersionCheckboxChanged(item, checked) {
			if (checked) {
				this.versionsSelected.push(item);
				// this function is called only for add new item, item.id is undefined in all series that we want to add
				this.itemsIdArray.push(this.item.id);
			} else {
				this.versionsSelected.splice(this.versionsSelected.indexOf(item), 1);
				this.itemsIdArray.pop(); // we can remove any element, because all elements are equal, the values are undefined
			}
		},

		productCategorySelectedHandler() {
			this.item.subcategoryId = null;
			this.item.subcategory = null;
			this.item.languageId = null;
			this.subcategoryId = null;
		},

		productSubCategorySelectedHandler() {
			// Need to set the subcategory for check if language is enabled
			this.item.subcategoryId = this.subcategoryId;

			if (this.item.subcategory && this.item.subcategory.hasImplicitLanguage) {
				this.item.languageId = this.getLanguageByCode(this.item.title.substr(5, 2)) ? this.getLanguageByCode(this.item.title.substr(5, 2)).id : null;
			}
		},

		showError(value) {
			this.errorMessage = value;
			this.loadingData = false;

			this.item.subcategoryId = null;
			this.item.subcategory = null;
			this.item.languageId = null;

			this.item.description = null;

			this.categoryId = null;
			this.subcategoryId = null;
		},
	},

	created() {
		if (this.item.hasValidFormatTitle) {
			this.categoryId = this.item.category.id;
			this.subcategoryId = this.item.subcategoryId;
		} else if (this.item.certificationId) {
			this.categoryId = this.item.category.id;
			this.subcategoryId = this.item.subcategoryId;
		}


		this.languages.sort(utilities.multiCriteriaSort(['title']));

		this.seriesAndVersionsList = this.series.sort(utilities.multiCriteriaSort(['title'])).reduce((acum, s) => acum.concat(...s.versions.map((v) => ({label: s.versions.length > 1 ? s.title.concat(` (v.${v.title})`) : s.title, seriesId: s.id, versionId: v.id, enabled: false, selected: false, title: '' }))), []);

		this.seriesAndVersionsList.sort(utilities.multiCriteriaSort(['label']));
		this.versionsSelected.push(this.seriesAndVersionsList.find((svl) => svl.versionId === this.version.id));
		this.itemsIdArray.push(this.item.id);

		if (this.item.certificationId) this.item.otherDocuments = true;
	},
};
</script>

<style lang="scss">
	.series-versions-document-included-list {
		box-shadow: 1px 1px 3px #999;
		max-height: 300px;
		overflow-y: auto;
		.ul {
			list-style-type: none;
		}
	}
</style>
